import React from 'react';
import {
  Trees,
  Flower2,
  Brain,
  Wallet,
  HeartHandshake,
  Leaf,
  Sparkles,
} from 'lucide-react';
import './Courses.css';

type CourseItem = {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
  items: string[];
  featured?: boolean;
};

const courseData: CourseItem[] = [
  {
    icon: <Brain className="course-card-icon" />,
    title: 'AI未來學習力夏令營',
    subtitle: '把學習的時間縮短，把孩子的可能性放大',
    description:
      'AI不是取代孩子，而是幫助孩子更快理解、更會提問、更能整理知識。透過AI工具、第一性原理與專題實作，培養未來真正需要的學習力、創造力與表達力。',
    items: ['AI工具入門', '縮短學習時間', '提問與整理能力', '專題創作發表'],
    featured: true,
  },
  {
    icon: <Trees className="course-card-icon" />,
    title: '森林體驗營',
    subtitle: '回到自然，回到自己',
    description:
      '以三天兩夜或單日活動形式，走入森林場域，透過安靜、散步、對話與生活體驗，重新整理人生節奏與方向。',
    items: ['森林散步', '靜心時間', '團體交流', '夜間沉澱'],
  },
  {
    icon: <Flower2 className="course-card-icon" />,
    title: '禪定與身心安定課',
    subtitle: '讓心安住下來',
    description:
      '不是宗教課程，而是透過簡單可實踐的方法，幫助人安靜、穩定情緒、減少焦慮，慢慢找回內在清明。',
    items: ['呼吸練習', '靜坐入門', '情緒整理', '安定身心'],
  },
  {
    icon: <Brain className="course-card-icon" />,
    title: '第一性原理思考課',
    subtitle: '從根本理解世界',
    description:
      '學習不只是接受現成答案，而是回到問題最核心的地方，重新拆解、重新思考，建立真正的理解力。',
    items: ['問題拆解', '核心邏輯', '本質思考', '重新建構'],
  },
  {
    icon: <Wallet className="course-card-icon" />,
    title: '安心理財課',
    subtitle: '不焦慮的長期財富觀',
    description:
      '不盯盤、不追高殺低，以簡單、穩定、長期的方式建立財富觀，讓理財回到生活，而不是讓生活被市場牽著走。',
    items: ['長期投資觀', '不看盤方法', '財務紀律', '安心配置'],
  },
  {
    icon: <HeartHandshake className="course-card-icon" />,
    title: '家庭陪伴 / 親子成長分享',
    subtitle: '陪伴，比說教更重要',
    description:
      '透過家庭關係、親子互動與人生價值分享，學習如何在理解、接納與陪伴中，建立更深的連結。',
    items: ['親子互動', '家庭關係', '同理溝通', '價值傳承'],
  },
  {
    icon: <Leaf className="course-card-icon" />,
    title: '自然農場體驗',
    subtitle: '與土地重新連結',
    description:
      '在自然場域中體驗飲食、勞動與生活節奏，從最基本的日常出發，感受健康、自然與生命的關係。',
    items: ['自然飲食', '農場生活', '土地體驗', '健康節奏'],
  },
];

const Course: React.FC = () => {
  return (
    <div className="course-page">
      {/* Hero */}
      <section className="course-hero">
        <div className="course-hero-overlay"></div>
        <div className="course-hero-content">
          <span className="course-badge">Courses</span>
          <h1>課程 / 活動</h1>
          <p>
            不是只是上課，
            <br />
            而是透過不同形式的體驗與陪伴，
            <br />
            幫助人回到更安定、更清楚、更有力量的生活。
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="course-intro section-width">
        <div className="course-section-header">
          <Sparkles className="course-section-icon" />
          <h2>課程類別</h2>
        </div>
        <p className="course-intro-text">
          森林學校的課程與活動，不以追求表面的成績為目標，
          而是希望透過自然、思考、陪伴與實踐，
          幫助人重新整理生命的根基。
        </p>
      </section>

      {/* Cards */}
      <section className="course-list section-width">
        <div className="course-grid">
          {courseData.map((course, index) => (
            <div className={`course-card ${course.featured ? 'course-card-featured' : ''}`} key={index}>
              <div className="course-card-top">
                {course.icon}
                <div>
                  <h3>{course.title}</h3>
                  <p className="course-subtitle">{course.subtitle}</p>
                </div>
              </div>

              <p className="course-description">{course.description}</p>

              <div className="course-divider"></div>

              <ul className="course-points">
                {course.items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
      {/* Wellness Section */}
      <section className="wellness-section section-width">
      <div className="course-section-header">
        <Sparkles className="course-section-icon" />
        <h2>Wellness & Cultural Experiences</h2>
      </div>

      <div className="wellness-grid">
        <div className="wellness-card">
          <h3>Clean Eating Guide</h3>

          <div className="video-container">
            <iframe
              src="https://www.youtube.com/embed/tQSNAgq7DPc"
              title="Clean Eating Guide 1"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>

          <div className="video-container">
            <iframe
              src="https://www.youtube.com/embed/uo-gmylvxD8"
              title="Clean Eating Guide 2"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </div>
      </section>
      {/* Closing */}
      <section className="course-closing section-width">
        <div className="course-closing-card">
          <h2>你可以從任何一門開始</h2>
          <p>
            不需要一次準備好所有答案，
            <br />
            只要願意開始，就已經走在回到自己的路上。
          </p>
        </div>
      </section>
    </div>
  );
};

export default Course;